import { useRef } from "react";
import { Editor } from "@hugerte/hugerte-react";
import type { Editor as HugeRTEEditor } from "hugerte";
import { useAgreementVariables } from "./useAgreementVariables";
import { htmlsEquivalent } from "../../../utils/htmlsEquivalent";

interface BodyEditorProps {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Full-agreement WYSIWYG body editor. Mirrors the toolbar/plugin set
 * the admin uses so existing agreements render the same after a save.
 */
export function BodyEditor({ value, onChange }: BodyEditorProps) {
  const { items } = useAgreementVariables();

  // itemsRef avoids a stale closure inside HugeRTE's `setup()` --
  // the editor only runs setup once but we want it to read the
  // latest variable list every time the menu opens.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  return (
    <Editor
      value={value || ""}
      onEditorChange={(content) => {
        if (htmlsEquivalent(content, value || "")) return;
        onChange(content);
      }}
      init={{
        height: "100%",
        menubar: true,
        plugins: [
          "lists",
          "table",
          "link",
          "code",
          "fullscreen",
          "searchreplace",
          "wordcount",
        ],
        // Sliding overflow keeps everything on one toolbar row with a
        // chevron for less-used items.
        toolbar_mode: "sliding",
        toolbar:
          "undo redo | blocks | bold italic underline strikethrough | " +
          "alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | link table | " +
          "fontfamily fontsize lineheight | " +
          "insertvariable | code fullscreen",
        setup: (editor: HugeRTEEditor) => {
          editor.ui.registry.addMenuButton("insertvariable", {
            text: "Insert Variable",
            fetch: (callback) => {
              const menuItems = itemsRef.current.map(
                (v) =>
                  ({
                    type: "menuitem" as const,
                    text: v.text,
                    onAction: () => editor.insertContent(v.value),
                  }),
              );
              callback(menuItems);
            },
          });
        },
        content_style:
          "body { font-family: 'Times New Roman', Times, serif; font-size: 14px; line-height: 1.6; }",
        promotion: false,
        branding: false,
        license_key: "gpl",
        line_height_formats: "1 1.15 1.4 1.6 1.8 2",
      }}
    />
  );
}
